import React, { useMemo } from "react";
import { View, TouchableOpacity, Text } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { AppText } from "@/components/AppText";
import { FontFamilies } from "@/constants/Typography";
import { useHotelRefund } from "@/hooks/useHotelRefund";
import { useSubscriptionRefund } from "@/hooks/useSubscriptionRefund";
import { useProductRefund } from "@/hooks/useProductRefund";
import { useFlightRefund } from "@/hooks/useFlightRefund";
import BillServices from "@/services/billServices";
import {
  RefundProcessItem,
  isHotelReservation,
  isUserSubscription,
  isPurchasedProduct,
  isFlightDelay,
  isBill,
  getPlatformName,
} from "@/utils/refundProcessTypeGuards";
import { ProductPrice } from "@/types/productPrice";
import { showError } from "@/utils/toast";

type Props = {
  item: RefundProcessItem;
  onClose: () => void;
  onRequestRefund: (_sessionId: string) => void;
  selectedPrice?: ProductPrice;
};

// Get description text based on item type
const getDescriptionText = (item: RefundProcessItem): string => {
  if (isHotelReservation(item)) {
    return "Sparrow's AI agent will call and negotiate a refund on your behalf for your hotel reservation.";
  }
  if (isUserSubscription(item)) {
    return "Sparrow's AI agent will call and negotiate a refund on your behalf for your subscription.";
  }
  if (isPurchasedProduct(item)) {
    return "Sparrow's AI agent will call and negotiate a refund on your behalf for your purchase.";
  }
  if (isFlightDelay(item)) {
    return "Sparrow's AI agent will call and negotiate a flight delay compensation on your behalf.";
  }
  if (isBill(item)) {
    return "Sparrow's AI agent will call and negotiate a refund on your behalf for your bill payment.";
  }

  throw new Error(
    `Unknown refund process item type with pk: ${(item as any).pk}`
  );
};

const ConfirmCallContent: React.FC<Props> = ({
  item,
  onRequestRefund,
  onClose,
}) => {
  const {
    initiateHotelRefund,
    isLoading: isHotelLoading,
    error: hotelError,
  } = useHotelRefund();
  const {
    initiateSubscriptionRefund,
    isLoading: isSubscriptionLoading,
    error: subscriptionError,
  } = useSubscriptionRefund();
  const {
    initiateProductRefund,
    isLoading: isProductLoading,
    error: productError,
  } = useProductRefund();
  const {
    initiateFlightRefund,
    isLoading: isFlightLoading,
    error: flightError,
  } = useFlightRefund();

  const isLoading =
    isHotelLoading ||
    isSubscriptionLoading ||
    isProductLoading ||
    isFlightLoading;
  const error = hotelError || subscriptionError || productError || flightError;

  const savingsAmount = useMemo(() => {
    if (
      isHotelReservation(item) &&
      item.hotelPrices &&
      Array.isArray(item.hotelPrices)
    ) {
      return Math.max(
        0,
        item.usdPrice -
          Math.min(
            ...item.hotelPrices.map(
              (price) => (price.price || 0) + (price.tax || 0)
            )
          )
      );
    }

    if (isPurchasedProduct(item) && item.lowerPriceProduct) {
      return Math.max(0, item.usdPrice - item.lowerPriceProduct.extractedPrice);
    }

    if (isUserSubscription(item)) {
      return item.usdPrice;
    }

    if (isFlightDelay(item)) {
      return item.delayNotice?.expectedCompensationInDollar || 0;
    }

    if (isBill(item)) {
      return item.totalAmount; // Use bill total as potential savings
    }

    return 0;
  }, [item]);

  const platformName = getPlatformName(item);

  const handleOnRequestRefund = async () => {
    try {
      if (isHotelReservation(item)) {
        const data = await initiateHotelRefund({
          confirmationNumber: item.confirmationNumber,
        });
        if (data) {
          onRequestRefund(data.session.sessionId);
        }
      } else if (isUserSubscription(item)) {
        const data = await initiateSubscriptionRefund({
          provider: item.provider,
          serviceName: item.serviceName,
          subscriptionDate: item.subscriptionDate,
        });
        if (data) {
          onRequestRefund(data.session.sessionId);
        }
      } else if (isPurchasedProduct(item)) {
        const data = await initiateProductRefund({
          productName: item.productName,
          orderNumber: item.orderNumber,
        });
        if (data) {
          onRequestRefund(data.session.sessionId);
        }
      } else if (isFlightDelay(item)) {
        const data = await initiateFlightRefund({
          confirmationNumber: item.confirmationNumber,
        });
        if (data) {
          onRequestRefund(data.session.sessionId);
        }
      } else if (isBill(item)) {
        const data = await BillServices.startRefund(
          item.accountNumber,
          item.billingDate
        );
        if (data) {
          onRequestRefund(data.session.sessionId);
        }
      } else {
        console.log("Unknown item type for refund");
      }
    } catch (error) {
      console.error('Refund request failed:', error);
      showError('Failed to initiate refund request. Please try again.');
      onClose();
    }
  };

  return (
    <>
      {/* Content */}
      <View style={styles.contentContainer}>
        <View style={styles.contentInner}>
          {/* Logo */}
          <View style={styles.platformLogo}>
            <Text style={styles.logoText}>
              {platformName?.charAt(0).toUpperCase()}
            </Text>
          </View>

          {/* Text content */}
          <View style={styles.textContainer}>
            <AppText style={styles.title}>
              Request a refund from{"\n"}
              {platformName}
              {savingsAmount > 0 ? ` (up to $${savingsAmount.toFixed(2)})` : ""}
            </AppText>

            <AppText style={styles.description}>
              {getDescriptionText(item)}
            </AppText>
          </View>
        </View>

        {/* Buttons */}
        <View style={styles.buttonContainer}>
          {error && (
            <View style={styles.errorContainer}>
              <AppText style={styles.errorText}>{error}</AppText>
            </View>
          )}

          <TouchableOpacity
            style={[styles.requestButton, isLoading && styles.disabledButton]}
            onPress={handleOnRequestRefund}
            disabled={isLoading}
          >
            <AppText style={styles.requestButtonText}>
              {isLoading ? "Processing..." : "Request refund"}
            </AppText>
          </TouchableOpacity>

          <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
            <AppText style={styles.cancelButtonText}>Cancel</AppText>
          </TouchableOpacity>
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    gap: 16,
    paddingTop: 56,
    paddingBottom: 24,
  },
  contentInner: {
    alignItems: "center",
    gap: 16,
    width: 300, // Fixed width from Figma
    alignSelf: "center",
  },
  platformLogo: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: "#000",
    justifyContent: "center",
    alignItems: "center",
  },
  logoText: {
    fontSize: 32,
    fontWeight: "700",
    color: "#FFF",
  },
  textContainer: {
    gap: 12,
    alignItems: "center",
    alignSelf: "stretch",
  },
  title: {
    fontFamily: FontFamilies.InterTight.Medium,
    fontSize: 24,
    fontWeight: "500",
    color: "#000000",
    textAlign: "center",
    lineHeight: 24 * 1.25, // 30px from Figma
  },
  description: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    fontWeight: "400",
    color: "#000000",
    opacity: 0.5,
    textAlign: "center",
    lineHeight: 15 * 1.5333, // 23px from Figma
    letterSpacing: -0.02 * 15, // -2% from Figma
  },
  buttonContainer: {
    gap: 8,
    alignSelf: "stretch",
  },
  requestButton: {
    backgroundColor: "#0F9129",
    borderRadius: 100,
    paddingHorizontal: 20,
    paddingVertical: 8,
    height: 58, // Updated to match Figma
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "stretch",
  },
  disabledButton: {
    backgroundColor: "#9CA3AF",
    opacity: 0.6,
  },
  requestButtonText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#FFFFFF",
    fontSize: 17,
    fontWeight: "600",
    textAlign: "center",
    lineHeight: 17 * 1.2941, // 22px from Figma
    height: 23, // Fixed height from Figma
  },
  cancelButton: {
    backgroundColor: "#F4F5F6",
    borderRadius: 100,
    paddingHorizontal: 20,
    paddingVertical: 8,
    height: 58, // Updated to match Figma
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "stretch",
  },
  cancelButtonText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#919191",
    fontSize: 17,
    fontWeight: "600",
    textAlign: "center",
    lineHeight: 17 * 1.2941, // 22px from Figma
    height: 23, // Fixed height from Figma
  },
  errorContainer: {
    backgroundColor: "#FEE2E2",
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  errorText: {
    color: "#DC2626",
    fontSize: 14,
    textAlign: "center",
    lineHeight: 20,
  },
});

export default ConfirmCallContent;
